import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { textScale } from '../../theme/standards';

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

interface GradientLoadingAnimationProps {
  message?: string;
  submessage?: string;
  variant?: 'card' | 'inline' | 'full';
}

export const GradientLoadingAnimation: React.FC<GradientLoadingAnimationProps> = ({
  message = 'Creating your travel plan...',
  submessage = 'This usually takes a few moments',
  variant = 'card',
}) => {
  const isCard = variant === 'card';
  const isFull = variant === 'full';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: isFull ? 8 : (isCard ? 6 : 3),
        px: isCard ? 4 : 2,
        width: '100%',
        ...(isCard && {
          borderRadius: 4,
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          backgroundColor: 'background.paper',
        }),
      }}
    >
      {/* Animated gradient bar */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          height: 6,
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          background: (theme) => alpha(theme.palette.divider, 0.3),
          mb: 4,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: (theme) => `linear-gradient(
              90deg,
              ${theme.palette.primary.main} 0%,
              ${theme.palette.secondary.main} 50%,
              ${theme.palette.primary.main} 100%
            )`,
            backgroundSize: '200% 200%',
            animation: `${gradientShift} 2s ease-in-out infinite`,
          }}
        />
        {/* Shimmer overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
            animation: `${shimmer} 1.5s ease-in-out infinite`,
          }}
        />
      </Box>

      {/* Animated logo/icon area */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          background: (theme) => `linear-gradient(
            135deg,
            ${alpha(theme.palette.primary.main, 0.15)} 0%,
            ${alpha(theme.palette.secondary.main, 0.15)} 100%
          )`,
        }}
      >
        {/* Pulsing ring */}
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            left: -8,
            right: -8,
            bottom: -8,
            borderRadius: '50%',
            border: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        />
        {/* Inner gradient icon */}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: (theme) => `linear-gradient(
              135deg,
              ${theme.palette.primary.main} 0%,
              ${theme.palette.secondary.main} 100%
            )`,
            backgroundSize: '200% 200%',
            animation: `${gradientShift} 3s ease-in-out infinite`,
          }}
        />
      </Box>

      <Stack spacing={1} alignItems="center">
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            background: (theme) => `linear-gradient(
              135deg,
              ${theme.palette.primary.main} 0%,
              ${theme.palette.secondary.main} 100%
            )`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundSize: '200% 200%',
            animation: `${gradientShift} 3s ease-in-out infinite`,
          }}
        >
          {message}
        </Typography>
        {submessage && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: textScale.support.fontSize,
            }}
          >
            {submessage}
          </Typography>
        )}
      </Stack>

      {/* Skeleton preview lines */}
      <Stack spacing={1.5} sx={{ width: '100%', maxWidth: 320, mt: 4 }}>
        {[0.8, 0.6, 0.7].map((width, index) => (
          <Box
            key={index}
            sx={{
              height: 12,
              borderRadius: 1,
              width: `${width * 100}%`,
              background: (theme) => alpha(theme.palette.divider, 0.4),
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)',
                animation: `${shimmer} 1.5s ease-in-out infinite`,
                animationDelay: `${index * 0.2}s`,
              }}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
